/**
 * Integrations API Service
 * 
 * Unified service for Trust-Drip integrations:
 * - Email (Gmail)
 * - Messages (various platforms)
 * - Social Media (Twitter, LinkedIn, etc.)
 * 
 * All require OAuth permission grants.
 */
import { getIdToken } from './firebase';

const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const NOTIFY_SERVICE_URL = import.meta.env.VITE_NOTIFY_SERVICE_URL || 'https://notify-service-buzbhnk2ha-uc.a.run.app';

// =============================================================================
// TYPES
// =============================================================================

export interface Email {
  id: string;
  from: string;
  to: string[];
  subject: string;
  snippet: string;
  body?: string;
  date: string;
  read: boolean;
  starred: boolean;
  labels: string[];
  has_attachments: boolean;
}

export interface Message {
  id: string;
  platform: 'sms' | 'whatsapp' | 'telegram' | 'slack' | 'discord';
  from: string;
  content: string;
  timestamp: string;
  read: boolean;
  conversation_id?: string;
}

export interface SocialNotification {
  id: string;
  platform: 'twitter' | 'linkedin' | 'instagram' | 'facebook';
  type: 'mention' | 'like' | 'comment' | 'follow' | 'dm' | 'share';
  from: string;
  content: string;
  timestamp: string;
  read: boolean;
  link?: string;
}

export interface IntegrationStatus {
  connected: boolean;
  last_sync?: string;
  error?: string;
  requires_reauth?: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

async function getHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// =============================================================================
// EMAIL API
// =============================================================================

/**
 * Get recent emails
 */
export async function getEmails(options?: {
  limit?: number;
  unread_only?: boolean;
  label?: string;
}): Promise<{
  success: boolean;
  emails: Email[];
  error?: string;
  permission_required?: boolean;
}> {
  try {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.unread_only) params.append('unread_only', 'true');
    if (options?.label) params.append('label', options.label);

    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/email/list?${params}`, {
      method: 'GET',
      headers
    });

    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        emails: [],
        permission_required: true,
        error: 'Gmail permission required. Connect your Google account to view emails.'
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch emails: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      emails: data.emails || []
    };
  } catch (error) {
    console.error('[IntegrationsAPI] getEmails error:', error);
    return {
      success: false,
      emails: [],
      error: error instanceof Error ? error.message : 'Failed to fetch emails'
    };
  }
}

/**
 * Send an email
 */
export async function sendEmail(
  to: string[],
  subject: string,
  body: string,
  options?: { cc?: string[]; bcc?: string[]; html?: boolean }
): Promise<{
  success: boolean;
  message_id?: string;
  error?: string;
  permission_required?: boolean;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/email/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ to, subject, body, ...options })
    });

    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        permission_required: true,
        error: 'Gmail permission required'
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to send email: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      message_id: data.message_id
    };
  } catch (error) {
    console.error('[IntegrationsAPI] sendEmail error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send email'
    };
  }
}

/**
 * Mark email as read/unread
 */
export async function markEmailRead(emailId: string, read: boolean): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/email/${emailId}/read`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ read })
    });

    return { success: response.ok };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update email'
    };
  }
}

// =============================================================================
// MESSAGES API
// =============================================================================

/**
 * Get recent messages across platforms
 */
export async function getMessages(options?: {
  platform?: Message['platform'];
  limit?: number;
  unread_only?: boolean;
}): Promise<{
  success: boolean;
  messages: Message[];
  error?: string;
  permission_required?: boolean;
}> {
  try {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    if (options?.platform) params.append('platform', options.platform);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.unread_only) params.append('unread_only', 'true');

    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/messages/list?${params}`, {
      method: 'GET',
      headers
    });

    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        messages: [],
        permission_required: true,
        error: 'Messaging permission required. Connect your messaging platforms.'
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      messages: data.messages || []
    };
  } catch (error) {
    console.error('[IntegrationsAPI] getMessages error:', error);
    return {
      success: false,
      messages: [],
      error: error instanceof Error ? error.message : 'Failed to fetch messages'
    };
  }
}

/**
 * Send a message to a platform
 */
export async function sendMessage(
  platform: Message['platform'],
  to: string,
  content: string
): Promise<{
  success: boolean;
  message_id?: string;
  error?: string;
  permission_required?: boolean;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/messages/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ platform, to, content })
    });

    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        permission_required: true,
        error: `${platform} permission required`
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to send message: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      message_id: data.message_id
    };
  } catch (error) {
    console.error('[IntegrationsAPI] sendMessage error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send message'
    };
  }
}

// =============================================================================
// SOCIAL MEDIA API
// =============================================================================

/**
 * Get social media notifications
 */
export async function getSocialNotifications(options?: {
  platform?: SocialNotification['platform'];
  limit?: number;
  unread_only?: boolean;
}): Promise<{
  success: boolean;
  notifications: SocialNotification[];
  error?: string;
  permission_required?: boolean;
}> {
  try {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    if (options?.platform) params.append('platform', options.platform);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.unread_only) params.append('unread_only', 'true');

    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/social/notifications?${params}`, {
      method: 'GET',
      headers
    });

    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        notifications: [],
        permission_required: true,
        error: 'Social media permission required. Connect your accounts.'
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      notifications: data.notifications || []
    };
  } catch (error) {
    console.error('[IntegrationsAPI] getSocialNotifications error:', error);
    return {
      success: false,
      notifications: [],
      error: error instanceof Error ? error.message : 'Failed to fetch notifications'
    };
  }
}

/**
 * Post to social media
 */
export async function postToSocial(
  platform: SocialNotification['platform'],
  content: string,
  options?: { media_url?: string; reply_to?: string }
): Promise<{
  success: boolean;
  post_id?: string;
  post_url?: string;
  error?: string;
  permission_required?: boolean;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/social/post`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ platform, content, ...options })
    });

    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        permission_required: true,
        error: `${platform} permission required`
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to post: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      post_id: data.post_id,
      post_url: data.post_url
    };
  } catch (error) {
    console.error('[IntegrationsAPI] postToSocial error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to post'
    };
  }
}

// =============================================================================
// INTEGRATION STATUS
// =============================================================================

/**
 * Get status of all integrations
 */
export async function getIntegrationStatus(): Promise<{
  email: IntegrationStatus;
  calendar: IntegrationStatus;
  messages: Record<Message['platform'], IntegrationStatus>;
  social: Record<SocialNotification['platform'], IntegrationStatus>;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/status`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to get status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[IntegrationsAPI] getIntegrationStatus error:', error);
    // Return disconnected status for all
    return {
      email: { connected: false },
      calendar: { connected: false },
      messages: {
        sms: { connected: false },
        whatsapp: { connected: false },
        telegram: { connected: false },
        slack: { connected: false },
        discord: { connected: false }
      },
      social: {
        twitter: { connected: false },
        linkedin: { connected: false },
        instagram: { connected: false },
        facebook: { connected: false }
      }
    };
  }
}

/**
 * Start OAuth flow for an integration
 */
export async function connectIntegration(
  type: 'email' | 'calendar' | 'messages' | 'social',
  platform?: string
): Promise<{
  success: boolean;
  auth_url?: string;
  error?: string;
  status?: string;
  message?: string;
  setup_instructions?: string;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/connect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, platform })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `Failed to start auth: ${response.status}`);
    }

    return {
      success: true,
      auth_url: data.auth_url,
      status: data.status,
      message: data.message,
      setup_instructions: data.setup_instructions
    };
  } catch (error) {
    console.error('[IntegrationsAPI] connectIntegration error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to start authentication'
    };
  }
}

export async function connectProvider(
  provider: string
): Promise<{
  success: boolean;
  auth_url?: string;
  error?: string;
  status?: string;
  message?: string;
  setup_instructions?: string;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/connect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type: provider, platform: provider })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `Failed to start auth: ${response.status}`);
    }

    return {
      success: true,
      auth_url: data.auth_url,
      status: data.status,
      message: data.message,
      setup_instructions: data.setup_instructions
    };
  } catch (error) {
    console.error('[IntegrationsAPI] connectProvider error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to start authentication'
    };
  }
}

/**
 * Disconnect an integration
 */
export async function disconnectIntegration(
  type: 'email' | 'calendar' | 'messages' | 'social',
  platform?: string
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, platform })
    });

    return { success: response.ok };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to disconnect'
    };
  }
}

// =============================================================================
// NOTIFICATIONS
// =============================================================================

/**
 * Get all unread notifications count
 */
export async function getUnreadCounts(): Promise<{
  email: number;
  messages: number;
  social: number;
  total: number;
}> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${NOTIFY_SERVICE_URL}/unread`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      return { email: 0, messages: 0, social: 0, total: 0 };
    }

    return await response.json();
  } catch {
    return { email: 0, messages: 0, social: 0, total: 0 };
  }
}
